if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/* ===== Quantity snap-to-step (global) ===== */
(() => {
  /**
   * Round UP to the next valid multiple of step.
   * Keeps 0 if allowZero is true.
   */
  function snapQtyToStep(rawValue, step, min, max, allowZero) {
    let value = Number(rawValue);

    if (!Number.isFinite(value)) value = allowZero ? 0 : (min || step || 1);

    // Prevent negatives
    if (value < 0) value = allowZero ? 0 : (min || step || 1);

    // Keep 0 if allowed
    if (allowZero && value === 0) return 0;

    // Enforce minimum (if not allowing 0)
    const effectiveMin = Number.isFinite(min) ? min : 1;
    if (!allowZero && value < effectiveMin) value = effectiveMin;

    // Snap UP to next multiple of step
    if (step > 1) {
      value = Math.ceil(value / step) * step;
    }

    // Enforce max (if present)
    if (Number.isFinite(max) && max > 0 && value > max) value = max;

    return value;
  }

  function getStep(input) {
    const ds = input.dataset?.qtyStep;
    const stepAttr = input.getAttribute('step');
    const step = Number(ds || stepAttr || 1);
    return Number.isFinite(step) && step > 0 ? step : 1;
  }

  function getMin(input) {
    const minAttr = input.getAttribute('min');
    const min = Number(minAttr);
    return Number.isFinite(min) ? min : NaN;
  }

  function getMax(input) {
    const maxAttr = input.getAttribute('max');
    const max = Number(maxAttr);
    return Number.isFinite(max) ? max : NaN;
  }

  function allowZero(input) {
    return input.dataset?.qtyAllowZero === 'true';
  }

  function normaliseQuantityInput(input) {
    if (!input) return;

    // Only touch number qty inputs
    if (input.type !== 'number') return;

    const step = getStep(input);
    if (step <= 1) return; // nothing to do

    const min = getMin(input);
    const max = getMax(input);
    const snapped = snapQtyToStep(input.value, step, min, max, allowZero(input));

    // Write back only if it changed (avoids extra events)
    if (String(snapped) !== String(input.value)) {
      input.value = snapped;

      // Notify any theme scripts listening for input
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  // 1) Snap on blur/change (covers click-away behaviour)
  document.addEventListener(
    'change',
    (e) => {
      const input = e.target;
      if (input && input.classList && input.classList.contains('quantity__input')) {
        normaliseQuantityInput(input);
      }
    },
    true
  );

  document.addEventListener(
    'blur',
    (e) => {
      const input = e.target;
      if (input && input.classList && input.classList.contains('quantity__input')) {
        normaliseQuantityInput(input);
      }
    },
    true
  );

  // 2) Snap on ANY add-to-cart form submit (product cards, quick add, bottom blocks, etc.)
  //    BUT do NOT interfere with cart update forms.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !(form instanceof HTMLFormElement)) return;

    // Add-to-cart forms usually include input[name="id"] (variant id)
    const hasVariantId = form.querySelector('input[name="id"]');

    // Cart update forms use updates[] / updates[variant_id]
    const isCartUpdate = form.querySelector('input[name^="updates"]');

    if (!hasVariantId || isCartUpdate) return;

    const qty = form.querySelector('input[name="quantity"].quantity__input');
    if (!qty) return;

    normaliseQuantityInput(qty);
  });
})();
